<template>
  <section class="panel">
    <el-tabs v-model="activeTab" @tab-click="tabChanged">
      <el-tab-pane label="公司简介" name="company" />
      <el-tab-pane label="首页轮播图" name="home-banners" />
      <el-tab-pane label="用户协议" name="user-agreement" />
      <el-tab-pane label="隐私协议" name="privacy-policy" />
      <el-tab-pane label="核验提示配置" name="verify-prompts" />
      <el-tab-pane label="防伪码配置" name="anti-code-qrcode" />
      <el-tab-pane label="会员等级配置" name="member-levels" />
    </el-tabs>

    <template v-if="activeTab === 'home-banners'">
      <div class="toolbar">
        <el-button type="primary" icon="el-icon-check" @click="saveCms">保存</el-button>
        <el-button icon="el-icon-plus" @click="addBanner">新增轮播图</el-button>
      </div>
      <div class="muted" style="margin-bottom:12px">
        建议上传横版图片，至少配置 1 张。未启用或未上传图片的轮播图不会在小程序首页展示。
      </div>
      <div class="banner-list">
        <div v-for="(item, index) in bannerRows" :key="item.localId" class="banner-editor">
          <div class="banner-preview">
            <image-upload v-model="item.imageUrl" :file-id.sync="item.fileId" biz-type="home-banner" />
          </div>
          <div class="banner-fields">
            <div class="form-grid">
              <el-form-item label="标题">
                <el-input v-model="item.title" placeholder="例如 官方正品保障" clearable />
              </el-form-item>
              <el-form-item label="排序">
                <el-input-number v-model="item.sort" :min="0" :step="10" />
              </el-form-item>
              <el-form-item label="状态">
                <el-select v-model="item.status" style="width:100%">
                  <el-option label="启用" value="ENABLED" />
                  <el-option label="禁用" value="DISABLED" />
                </el-select>
              </el-form-item>
              <el-form-item label="跳转类型">
                <el-select v-model="item.targetType" clearable placeholder="不跳转" style="width:100%">
                  <el-option label="产品详情" value="product" />
                  <el-option label="活动详情" value="activity" />
                  <el-option label="固定页面" value="cms" />
                  <el-option label="产品分类" value="category" />
                </el-select>
              </el-form-item>
              <el-form-item label="目标 ID / 页面 Key">
                <el-input v-model="item.targetId" placeholder="产品/活动/分类 ID，或 company 等页面 Key" clearable />
              </el-form-item>
            </div>
          </div>
          <div class="banner-actions">
            <el-button icon="el-icon-top" circle size="mini" :disabled="index === 0" @click="moveBanner(index, -1)" />
            <el-button icon="el-icon-bottom" circle size="mini" :disabled="index === bannerRows.length - 1" @click="moveBanner(index, 1)" />
            <el-button type="danger" icon="el-icon-delete" circle size="mini" @click="removeBanner(index)" />
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="activeTab === 'verify-prompts'">
      <div class="toolbar">
        <el-button type="primary" icon="el-icon-check" @click="saveCms">保存</el-button>
        <el-button icon="el-icon-refresh-left" @click="restoreVerifyPrompts">恢复默认文案</el-button>
      </div>
      <el-alert
        title="每种核验结果均可独立配置标题、主提示、副提示和温馨提示；留空后小程序将自动使用系统默认文案。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
      />
      <div class="verify-prompt-grid">
        <el-card v-for="item in verifyPromptItems" :key="item.key" class="verify-prompt-card" shadow="never">
          <div slot="header" class="verify-prompt-heading">
            <span>{{ item.label }}</span>
            <span class="muted">{{ item.description }}</span>
          </div>
          <el-form label-position="top">
            <el-form-item label="标题"><el-input v-model="verifyPrompts[item.key].title" maxlength="30" show-word-limit /></el-form-item>
            <el-form-item label="主提示"><el-input v-model="verifyPrompts[item.key].mainText" type="textarea" :rows="2" maxlength="120" show-word-limit /></el-form-item>
            <el-form-item label="副提示"><el-input v-model="verifyPrompts[item.key].subText" type="textarea" :rows="2" maxlength="160" show-word-limit /></el-form-item>
            <el-form-item label="温馨提示"><el-input v-model="verifyPrompts[item.key].tip" type="textarea" :rows="2" maxlength="160" show-word-limit /></el-form-item>
          </el-form>
        </el-card>
      </div>
    </template>

    <template v-else-if="activeTab === 'anti-code-qrcode'">
      <div class="toolbar">
        <el-button type="primary" icon="el-icon-check" @click="saveCms">保存</el-button>
      </div>
      <el-alert
        title="上传的 PNG/JPG Logo 会居中显示在之后新生成的防伪二维码中。二维码使用高纠错级别，请上传清晰、边缘留白充足的方形或横向 Logo。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
      />
      <el-form label-position="top" style="max-width: 460px">
        <el-form-item label="二维码中心 Logo">
          <image-upload v-model="form.coverUrl" :file-id.sync="form.coverFileId" accept="image/png,image/jpeg" biz-type="anti-code-qrcode-logo" />
        </el-form-item>
      </el-form>
    </template>

    <template v-else-if="activeTab !== 'member-levels'">
      <div class="toolbar">
        <el-button type="primary" icon="el-icon-check" @click="saveCms">保存</el-button>
      </div>
      <el-form label-position="top">
        <el-form-item label="标题"><el-input v-model="form.title" /></el-form-item>
        <el-form-item v-if="activeTab === 'company'" label="首页公司简介右侧品牌主视觉图">
          <image-upload v-model="form.coverUrl" :file-id.sync="form.coverFileId" biz-type="cms-company" />
        </el-form-item>
        <el-form-item label="内容">
          <rich-text-editor
            v-model="form.content"
            :placeholder="contentPlaceholder"
            :biz-type="`cms-${activeTab}`"
          />
        </el-form-item>
      </el-form>
    </template>

    <template v-else>
      <div class="toolbar">
        <el-input v-model="ruleQuery.keyword" placeholder="等级名称/编码" clearable style="width:200px" />
        <el-select v-model="ruleQuery.status" placeholder="状态" clearable style="width:130px">
          <el-option label="启用" value="ENABLED" />
          <el-option label="禁用" value="DISABLED" />
        </el-select>
        <el-button class="btn-search" icon="el-icon-search" @click="searchRules">查询</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openRule()">新增等级规则</el-button>
        <el-button class="btn-refresh" icon="el-icon-refresh" @click="recalculateUsers">重新计算用户等级</el-button>
      </div>
      <div class="muted" style="margin-bottom:12px">
        等级按“注册时长（天）+ 认证产品次数”计算；认证产品次数按用户首次认证成功的不同防伪码去重统计。
      </div>
      <el-table :data="rulePage.records" border size="small">
        <el-table-column prop="levelCode" label="等级编码" width="130" />
        <el-table-column prop="levelName" label="等级名称" min-width="140" />
        <el-table-column prop="minRegisterDays" label="注册时长≥天" width="130" />
        <el-table-column prop="minVerifiedCount" label="认证产品≥次" width="130" />
        <el-table-column prop="sort" label="等级权重" width="110" />
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 'ENABLED' ? 'success' : 'info'" size="small">{{ row.status === 'ENABLED' ? '启用' : '禁用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180">
          <template slot-scope="{ row }">
            <el-button size="mini" class="btn-edit" icon="el-icon-edit" @click="openRule(row)">编辑</el-button>
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeRule(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next"
        :current-page="rulePage.page"
        :page-size="rulePage.size"
        :page-sizes="[10, 20, 50]"
        :total="rulePage.total"
        @size-change="size => { ruleQuery.size = size; searchRules() }"
        @current-change="p => { ruleQuery.page = p; loadRules() }"
      />
    </template>

    <el-dialog :title="ruleForm.id ? '编辑会员等级规则' : '新增会员等级规则'" :visible.sync="ruleDialog" width="560px">
      <el-form label-position="top">
        <el-form-item label="等级编码"><el-input v-model="ruleForm.levelCode" placeholder="例如 NORMAL / VIP" /></el-form-item>
        <el-form-item label="等级名称"><el-input v-model="ruleForm.levelName" placeholder="例如 普通会员 / VIP会员" /></el-form-item>
        <div class="form-grid">
          <el-form-item label="注册时长至少（天）"><el-input-number v-model="ruleForm.minRegisterDays" :min="0" /></el-form-item>
          <el-form-item label="认证产品至少（次）"><el-input-number v-model="ruleForm.minVerifiedCount" :min="0" /></el-form-item>
          <el-form-item label="等级权重"><el-input-number v-model="ruleForm.sort" :min="0" /></el-form-item>
          <el-form-item label="状态">
            <el-select v-model="ruleForm.status" style="width:100%">
              <el-option label="启用" value="ENABLED" />
              <el-option label="禁用" value="DISABLED" />
            </el-select>
          </el-form-item>
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="ruleDialog = false">取消</el-button>
        <el-button type="primary" @click="saveRule">保存</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import http from '../api'
import ImageUpload from '../components/ImageUpload.vue'
import RichTextEditor from '../components/RichTextEditor.vue'

const cmsKeys = ['company', 'home-banners', 'user-agreement', 'privacy-policy', 'verify-prompts', 'anti-code-qrcode']
const blankPage = { total: 0, page: 1, size: 10, records: [] }
const defaultVerifyPrompts = () => ({
  success: {
    title: '正品认证通过',
    mainText: '您所查验的 YOGI/THOR 产品为官方正规出品',
    subText: '本编码首次核验，无异常扫码记录',
    tip: '温馨提示：防伪码仅可核验一次，谨防流通仿冒'
  },
  selfRepeat: {
    title: '本人重复核验',
    mainText: '该防伪码首次核验人为您本人',
    subText: '本次为重复核验，系统已单独识别并记录',
    tip: '系统识别到首次核验与当前账号一致，请放心保留本次复查记录'
  },
  repeat: {
    title: '查验异常',
    mainText: '该防伪码已被认证，不属于首次核验',
    subText: '如您为产品首次购买者，建议核对包装完整性，及时联系售后客服排查真伪',
    tip: '请保留产品包装与购买凭证，必要时联系官方售后进一步核实'
  },
  productMissing: {
    title: '产品不存在',
    mainText: '未查询到该防伪码对应的有效产品信息',
    subText: '请核对包装上的官方防伪码后重新扫码',
    tip: '该码不存在或已失效，无法进行产品绑定'
  },
  abnormal: {
    title: '查验异常',
    mainText: '该防伪码暂未通过系统核验',
    subText: '请确认扫描的是官方防伪码，如仍有疑问，请联系官方客服',
    tip: '请保留产品包装与购买凭证，必要时联系官方售后进一步核实'
  }
})
const verifyPromptItems = [
  { key: 'success', label: '首次正品核验', description: '有效防伪码首次核验成功' },
  { key: 'selfRepeat', label: '本人重复核验', description: '首次核验人为当前账号' },
  { key: 'repeat', label: '非本人重复核验', description: '防伪码已被其他账号核验' },
  { key: 'productMissing', label: '产品/防伪码不存在', description: '未找到有效且在售的产品信息' },
  { key: 'abnormal', label: '参数或其他异常', description: '二维码参数缺失、格式异常等情况' }
]

export default {
  components: { ImageUpload, RichTextEditor },
  data() {
    return {
      activeTab: 'company',
      form: {},
      bannerRows: [],
      verifyPrompts: defaultVerifyPrompts(),
      verifyPromptItems,
      ruleQuery: { page: 1, size: 10 },
      rulePage: { ...blankPage },
      ruleDialog: false,
      ruleForm: {}
    }
  },
  mounted() {
    this.loadCms()
  },
  computed: {
    contentPlaceholder() {
      const labels = {
        company: '请输入公司简介，可插入图片、表格、链接等',
        'home-banners': '请配置小程序首页顶部轮播图',
        'user-agreement': '请输入用户协议内容，可插入图片、表格、链接等',
        'privacy-policy': '请输入隐私协议内容，可插入图片、表格、链接等'
      }
      return labels[this.activeTab] || '请输入内容，可插入图片、表格、链接等'
    }
  },
  methods: {
    tabChanged() {
      if (this.activeTab === 'member-levels') {
        this.loadRules()
      } else {
        this.loadCms()
      }
    },
    async loadCms() {
      this.form = (await http.get(`/admin/cms/${this.activeTab}`)) || { pageKey: this.activeTab, title: '', content: '' }
      if (this.activeTab === 'home-banners') {
        this.bannerRows = this.parseBanners(this.form.content)
      } else if (this.activeTab === 'verify-prompts') {
        this.verifyPrompts = this.parseVerifyPrompts(this.form.content)
      }
    },
    async saveCms() {
      if (this.activeTab === 'home-banners') {
        this.form.title = this.form.title || '首页轮播图'
        this.form.content = JSON.stringify(this.bannerRows.map((item, index) => ({
          title: item.title || '',
          imageUrl: item.imageUrl || '',
          fileId: item.fileId || null,
          sort: Number.isFinite(Number(item.sort)) ? Number(item.sort) : (index + 1) * 10,
          status: item.status || 'ENABLED',
          targetType: item.targetType || '',
          targetId: item.targetId || ''
        })))
      } else if (this.activeTab === 'verify-prompts') {
        this.form.title = '核验提示配置'
        this.form.content = JSON.stringify(this.verifyPrompts)
      } else if (this.activeTab === 'anti-code-qrcode') {
        this.form.title = '防伪码配置'
        this.form.content = ''
      }
      await http.put(`/admin/cms/${this.activeTab}`, this.form)
      this.$message.success('系统配置已保存')
    },
    parseBanners(content) {
      try {
        const rows = JSON.parse(content || '[]')
        if (Array.isArray(rows) && rows.length) {
          return rows.map((item, index) => this.normalizeBanner(item, index))
        }
      } catch (e) {
        // ignore invalid legacy content
      }
      return [this.normalizeBanner({}, 0)]
    },
    normalizeBanner(item, index) {
      return {
        localId: `${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
        title: item.title || '',
        imageUrl: item.imageUrl || '',
        fileId: item.fileId || null,
        sort: item.sort == null ? (index + 1) * 10 : Number(item.sort),
        status: item.status || 'ENABLED',
        targetType: item.targetType || '',
        targetId: item.targetId == null ? '' : String(item.targetId)
      }
    },
    parseVerifyPrompts(content) {
      const defaults = defaultVerifyPrompts()
      try {
        const configured = JSON.parse(content || '{}')
        return Object.keys(defaults).reduce((result, key) => {
          result[key] = { ...defaults[key], ...(configured[key] || {}) }
          return result
        }, {})
      } catch (e) {
        return defaults
      }
    },
    restoreVerifyPrompts() {
      this.verifyPrompts = defaultVerifyPrompts()
      this.$message.success('已恢复默认文案，请点击保存后生效')
    },
    addBanner() {
      this.bannerRows.push(this.normalizeBanner({}, this.bannerRows.length))
    },
    removeBanner(index) {
      this.bannerRows.splice(index, 1)
      if (!this.bannerRows.length) {
        this.addBanner()
      }
    },
    moveBanner(index, step) {
      const target = index + step
      if (target < 0 || target >= this.bannerRows.length) return
      const rows = [...this.bannerRows]
      const current = rows.splice(index, 1)[0]
      rows.splice(target, 0, current)
      this.bannerRows = rows.map((item, itemIndex) => ({ ...item, sort: (itemIndex + 1) * 10 }))
    },
    async loadRules() {
      this.rulePage = await http.get('/admin/member-level-rules', { params: this.ruleQuery })
    },
    searchRules() {
      this.ruleQuery.page = 1
      this.loadRules()
    },
    openRule(row) {
      this.ruleForm = row ? { ...row } : { levelCode: '', levelName: '', minRegisterDays: 0, minVerifiedCount: 0, sort: 0, status: 'ENABLED' }
      this.ruleDialog = true
    },
    async saveRule() {
      if (!this.ruleForm.levelCode || !this.ruleForm.levelName) {
        this.$message.warning('请填写等级编码和等级名称')
        return
      }
      if (this.ruleForm.id) {
        await http.put(`/admin/member-level-rules/${this.ruleForm.id}`, this.ruleForm)
      } else {
        await http.post('/admin/member-level-rules', this.ruleForm)
      }
      this.ruleDialog = false
      this.$message.success('会员等级规则已保存')
      this.loadRules()
    },
    async removeRule(row) {
      await this.$confirm(`确认删除会员等级规则“${row.levelName}”？`)
      await http.delete(`/admin/member-level-rules/${row.id}`)
      this.loadRules()
    },
    async recalculateUsers() {
      await this.$confirm('确认按当前启用规则重新计算所有用户会员等级？')
      const data = await http.post('/admin/member-level-rules/recalculate-users')
      this.$message.success(`已重新计算 ${data.updated} 个用户`)
    }
  },
  watch: {
    activeTab(value) {
      if (!cmsKeys.includes(value) && value !== 'member-levels') {
        this.activeTab = 'company'
      }
    }
  }
}
</script>

<style scoped>
.banner-list {
  display: grid;
  gap: 14px;
}

.verify-prompt-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.verify-prompt-card {
  min-width: 0;
}

.verify-prompt-card ::v-deep .el-form-item {
  margin-bottom: 14px;
}

.verify-prompt-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 600;
}

.verify-prompt-heading .muted {
  font-size: 12px;
  font-weight: 400;
}

.banner-editor {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 112px;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border: 1px solid #e6eef5;
  border-radius: 8px;
  background: #fff;
}

.banner-preview {
  min-height: 128px;
  display: flex;
  align-items: center;
}

.banner-preview ::v-deep .image-upload {
  flex-direction: column;
  align-items: flex-start;
}

.banner-preview ::v-deep .image-upload-preview {
  width: 200px;
  height: 96px;
}

.banner-fields ::v-deep .el-form-item {
  margin-bottom: 0;
}

.banner-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 980px) {
  .banner-editor {
    grid-template-columns: 1fr;
  }

  .banner-actions {
    justify-content: flex-start;
  }

  .verify-prompt-grid {
    grid-template-columns: 1fr;
  }
}
</style>
